import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageBanner from '../components/PageBanner';
import ReservationSection from '../components/ReservationSection';
import { SITE_CONTENT } from '../content';

/* ─────────────────────────────────────────
   useFadeIn — triggers when el enters viewport
───────────────────────────────────────── */
const useFadeIn = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

/* ─────────────────────────────────────────
   MenuRow
───────────────────────────────────────── */
const MenuRow = ({ item, index }) => (
  <div
    className="group py-5 border-b border-[#0A230E]/10 last:border-0"
    style={{ animationDelay: `${index * 60}ms` }}
  >
    <div className="flex items-baseline justify-between gap-4">
      <h4 className="text-[#0A230E] font-semibold text-[21px] tracking-wide transition-colors duration-300">
        {item.name}
      </h4>
      <span className="shrink-0 text-[#0A230E] font-semibold text-base">{item.price}</span>
    </div>
    <p className="text-[#0A230E] font-medium text-sm mt-1 leading-relaxed max-w-sm">{item.desc}</p>
  </div>
);

/* ─────────────────────────────────────────
   MenuSection — image + list block
   alternates image side each section
───────────────────────────────────────── */
const MenuSection = ({ section, index }) => {
  const [ref, visible] = useFadeIn();
  const isEven = index % 2 === 0;

  return (
    <section
      ref={ref}
      className={`flex flex-col lg:flex-row gap-0 lg:gap-16 items-stretch mb-24 transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${!isEven ? 'lg:flex-row-reverse' : ''}`}
    >
      {/* ── Image column ── */}
      <div className="lg:w-[38%] shrink-0 overflow-hidden rounded-xl">
        <img
          src={section.image || SITE_CONTENT.pages.menuPage.menuImages.default}
          alt={section.label}
          className="w-full h-full object-cover object-center min-h-[340px] lg:min-h-[480px] hover:scale-105 transition-transform duration-700 ease-out"
        />
      </div>

      {/* ── List column ── */}
      <div className="flex-1 py-8 lg:py-2 flex flex-col justify-center">
        {/* Category label */}
        <p className="text-white/35 text-[10px] tracking-[0.35em] uppercase mb-2">
          Navabi Restaurant
        </p>
        <h2 className="text-[#0A230E] font-light text-3xl sm:text-4xl tracking-wide mb-2">
          {section.label}
        </h2>
        {/* Thin gold rule */}
        <div className="w-10 h-[1px] bg-amber-400/60 mb-7" />

        {/* Items */}
        <div>
          {section.items.map((item, i) => (
            <MenuRow key={item.name} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────
   MenuPage
───────────────────────────────────────── */
const MenuPage = () => {
  const { pageBanner, intro, sections } = SITE_CONTENT.pages.menuPage;
  const categories = ['All', ...sections.map((s) => s.label)];

  const [activeCategory, setActiveCategory] = useState('All');

  const visibleSections =
    activeCategory === 'All'
      ? sections
      : sections.filter((s) => s.label === activeCategory);

  return (
    <div className="min-h-screen bg-[#f0eee5] text-white">
      <Header />

      {/* ── Hero / Page Banner ── */}
      <PageBanner
        bgImage={pageBanner.bgImage}
        eyebrow={pageBanner.eyebrow}
        title={pageBanner.title}
        breadcrumbs={pageBanner.breadcrumbs}
      />

      {/* ── Menu Content ── */}
      <main className="">

        {/* Intro heading */}
        <div className='max-w-7xl mx-auto px-6 lg:px-10 py-20'>
        <div className="text-center mb-14">
          <p className="text-[#0A230E] text-[11px] tracking-[0.35em] uppercase mb-3">
            {intro.eyebrow}
          </p>
          <h2 className="text-[#0A230E] font-bold text-3xl sm:text-4xl tracking-wide">
            {intro.title} <span className="italic">{intro.titleItalic}</span>
          </h2>
          <div className="w-10 h-[1px] bg-[#0A230E] mx-auto mt-5" />
        </div>

        {/* Category Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-20">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-6 py-2 rounded-full text-xs tracking-[0.18em] uppercase font-medium border transition-all duration-300 ${
                activeCategory === cat
                  ? 'bg-[#0A230E] text-white border-[#0A230E] shadow-lg shadow-white/10'
                  : 'border-[#0A230E]/20 text-[#0A230E]/55 hover:border-[#0A230E]/50 hover:text-[#0A230E]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* ── Two-column menu sections ── */}
        {visibleSections.map((section, idx) => (
          <MenuSection key={section.id} section={section} index={idx} />
        ))}
        </div>
        <ReservationSection />
      </main>

      <Footer />
    </div>
  );
};

export default MenuPage;